// Eras on the history map. An era (Reconstruction, the Cold War) is a long,
// spread-out period with no single place, so it gets no marker. Its region
// is the countries in its entity locations; an era with none is worldwide.
// The map lists the eras of the day whose countries are in view.

export const ERAS_LISTED = 8;

export const eraRegionIds = (era) =>
  [...new Set((era.locations || []).filter((l) => l.kind === "entity").map((l) => l.entity_id))];

// "1865–1877"
export const eraYears = (era) => {
  const s = era.start_date.slice(0, 4);
  const e = era.end_date ? era.end_date.slice(0, 4) : s;
  return s === e ? s : `${s}–${e}`;
};

// [west, south, east, north] of each polygon of a GeoJSON shape, kept per
// shape. One box per polygon: a state with far-off islands (the United
// States with Guam) would otherwise span the whole world.
const boxCache = new WeakMap();
function polygonBoxes(geojson) {
  if (!geojson) return [];
  if (boxCache.has(geojson)) return boxCache.get(geojson);
  const boxes = [];
  const ringBox = (rings) => {
    const box = [Infinity, Infinity, -Infinity, -Infinity];
    rings[0].forEach(([x, y]) => {
      box[0] = Math.min(box[0], x);
      box[1] = Math.min(box[1], y);
      box[2] = Math.max(box[2], x);
      box[3] = Math.max(box[3], y);
    });
    if (box[0] !== Infinity) boxes.push(box);
  };
  const visit = (g) => {
    if (!g) return;
    if (g.type === "FeatureCollection") g.features.forEach((f) => visit(f.geometry));
    else if (g.type === "Feature") visit(g.geometry);
    else if (g.type === "GeometryCollection") g.geometries.forEach(visit);
    else if (g.type === "Polygon") ringBox(g.coordinates);
    else if (g.type === "MultiPolygon") g.coordinates.forEach(ringBox);
  };
  visit(geojson);
  boxCache.set(geojson, boxes);
  return boxes;
}

// Entity ids with a border on screen. The view can wrap around the
// antimeridian, so each box is also tried one world to either side.
function entitiesInView(map, boundaries) {
  const b = map.getBounds();
  const [west, south, east, north] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  const ids = new Set();
  boundaries.forEach((boundary) => {
    if (boundary.claim_kind !== "de_facto") return;
    if (ids.has(boundary.entity_id)) return;
    const onScreen = polygonBoxes(boundary.geojson).some(
      (box) =>
        box[1] <= north &&
        box[3] >= south &&
        (east - west >= 360 || [-360, 0, 360].some((shift) => box[0] + shift <= east && box[2] + shift >= west))
    );
    if (onScreen) ids.add(boundary.entity_id);
  });
  return ids;
}

// The eras of the day for the view: worldwide ones and those with a country
// on screen, most important first.
export function erasInView(map, events, boundaries, iso) {
  const eras = events.filter((ev) => ev.era && ev.start_date <= iso && iso <= (ev.end_date || ev.start_date));
  if (!eras.length) return [];
  const visible = entitiesInView(map, boundaries);
  return eras
    .filter((era) => {
      const region = eraRegionIds(era);
      return region.length === 0 || region.some((id) => visible.has(id));
    })
    .sort((a, b) => (b.importance || 0) - (a.importance || 0) || a.start_date.localeCompare(b.start_date));
}
